import { View, StyleSheet, Platform } from 'react-native';

const isWeb = Platform.OS === 'web';
import React, { useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { isTablet } from 'react-native-device-info';
import { iconSize, s, v } from '../../../utils/libs';
import { useAppSelector } from '../../../redux/helper';
import HeaderIconButton from './HeaderIconButton';
import HeaderInternetStatusIcon from './HeaderInternetStatusIcon';
import ChatwootService from '../../../services/ChatwootService';
import { requestNotificationsOnUserGesture } from '../../../services/requestNotificationsOnUserGesture';
import useChatwootSnapshot from '../../../hooks/useChatwootSnapshot';
import SupportUnreadBadge from '../../support/SupportUnreadBadge';
import { isEmployeeAdminRole } from '../../../utils/roles';

const tablet = isTablet();
const HEADER_ICON_SIZE = iconSize(tablet ? 24 : 22);

const HomeHeader = () => {
  const employee = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const unreadCount = useAppSelector(
    state => state.notifications.items.filter(item => !item.isRead).length,
  );
  const navigation = useNavigation<any>();
  const chatwootSnapshot = useChatwootSnapshot();
  const isAdmin = isEmployeeAdminRole(employee?.bp_employee_role);

  const notificationIconName = useMemo(
    () => (unreadCount > 0 ? 'notifications' : 'notifications-outline'),
    [unreadCount],
  );
  const notificationColor = unreadCount > 0 ? '#FF9800' : '#fff';
  const unreadDotVisible = unreadCount > 0;

  const handleNavigateNotifications = () => {
    void requestNotificationsOnUserGesture();
    const parentNavigation = navigation.getParent?.();
    if (parentNavigation) {
      parentNavigation.navigate('Notifications');
      return;
    }
    navigation.navigate('Notifications');
  };

  const handleNavigateSettings = () => {
    const parentNavigation = navigation.getParent?.();
    if (parentNavigation) {
      parentNavigation.navigate('SettingsScreen');
      return;
    }
    navigation.navigate('SettingsScreen');
  };

  const handleOpenSupportChat = () => {
    void ChatwootService.openChat();
  };

  return (
    <View style={styles.container}>
      <HeaderInternetStatusIcon offlineOnly={isAdmin} />
      {isAdmin ? (
        <HeaderIconButton
          tooltip="Customer support"
          style={styles.iconWrap}
          onPress={handleOpenSupportChat}
          accessibilityRole="button"
          accessibilityLabel="Open customer support chat">
          <View style={styles.supportIconWrap}>
            <Ionicons
              name="chatbubbles-sharp"
              size={HEADER_ICON_SIZE}
              color="#fff"
            />
            {chatwootSnapshot.unread.hasUnread ? (
              <SupportUnreadBadge
                count={chatwootSnapshot.unread.unreadCount}
                size="small"
              />
            ) : null}
          </View>
        </HeaderIconButton>
      ) : null}
      <HeaderIconButton
        tooltip="Notifications"
        style={styles.iconWrap}
        onPress={handleNavigateNotifications}
        accessibilityRole="button"
        accessibilityLabel="Open notifications">
        <Ionicons
          name={notificationIconName}
          size={HEADER_ICON_SIZE}
          color={notificationColor}
        />
        {unreadDotVisible && <View style={styles.unreadDot} />}
      </HeaderIconButton>
      <HeaderIconButton
        tooltip="Settings"
        style={styles.iconWrap}
        onPress={handleNavigateSettings}
        accessibilityRole="button"
        accessibilityLabel="Open settings">
        <Ionicons
          name="settings-outline"
          size={HEADER_ICON_SIZE}
          color="#fff"
        />
      </HeaderIconButton>
    </View>
  );
};

export default HomeHeader;

const iconWrapSize = isWeb ? 40 : v(36);

const styles = StyleSheet.create({
  container: {
    height: isWeb ? 44 : v(36),
    alignItems: 'center',
    gap: s(3),
    flexDirection: 'row',
    paddingRight: s(8),
  },
  iconWrap: {
    width: iconWrapSize,
    height: iconWrapSize,
    borderRadius: iconWrapSize / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  supportIconWrap: {
    position: 'relative',
  },
  unreadDot: {
    position: 'absolute',
    top: v(5),
    right: s(4),
    backgroundColor: '#F44336',
    width: s(8),
    height: s(8),
    borderRadius: s(4),
  },
});
